/**
 * INP 监控器
 */
export class INPMonitor {
  // 采样率 (0-1)
  samplingRate: number;
  // 最低持续时间阈值 (ms)
  minDuration: number;
  // 采集的最大交互次数
  maxInteractions: number;
  // 采集的最小交互次数
  minInteractions: number;
  isActive: boolean;
  startTimestamp: number;
  interactions: number[];
  performanceObserver: PerformanceObserver;
  eventListeners: Map<any, any>;
  finishTimestamp: number;
  report: (metric: any) => void;
  constructor() {
    // 配置参数
    this.samplingRate = 1;
    this.minDuration = 40;
    this.maxInteractions = 100;
    this.minInteractions = 10;

    // 监控状态
    this.isActive = false;
    this.startTimestamp = 0;
    this.interactions = [];
    this.finishTimestamp = 0;

    // 观察器实例
    this.performanceObserver = null;
    this.eventListeners = new Map();
  }

  /**
   * 1. 开启监控 - 开始收集交互指标和计算逻辑
   */
  start(report: (metric: any) => void) {
    if (this.isActive) {
      return;
    }
    this.report = report;
    this.isActive = true;
    this.startTimestamp = Date.now();
    this.interactions = [];

    this.setupPerformanceObserver();

    hbLogger.debug('INP 监控已启动');
  }

  /**
   * 设置 PerformanceObserver 监控事件性能
   */
  setupPerformanceObserver() {
    if (!window.PerformanceObserver) {
      this.destroy();
      return;
    }

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: PerformanceEventTiming) => {
          if (this.isValidInteraction(entry)) {
            hbLogger.debug('记录INP交互:', entry);
            this.recordInteraction(entry.duration);
          }
        });
      });

      this.performanceObserver.observe({
        type: 'event',
        buffered: true,
        durationThreshold: this.minDuration
      });
    } catch (error) {
      hbLogger.warn(`INP采集，PerformanceObserver 启动失败: ${error.message}`);
    }
  }

  /**
   * 记录交互数据
   */
  recordInteraction(interaction: number) {
    if (!this.isActive) return;

    this.interactions.push(interaction);
    if (this.interactions.length >= this.maxInteractions) {
      this.stopObserver();
      this.finishTimestamp = Date.now();
    }
  }

  /**
   * 2. 获取当前INP指标
   */
  getMetrics() {
    if (!this.isActive || this.interactions.length < this.minInteractions) {
      return null;
    }

    // 按持续时间排序
    const sortedDurations = this.interactions.sort((a, b) => a - b);

    const count = sortedDurations.length;
    const p75Index = Math.floor(count * 0.75);
    const p95Index = Math.floor(count * 0.95);
    const finishTime = this.finishTimestamp || Date.now();

    const report = {
      type: 'ymc',
      name: 'INP',
      value: sortedDurations[p75Index],
      p95: sortedDurations[p95Index],
      worst: sortedDurations[count - 1],
      count: count,
      startTime: this.startTimestamp,
      finishTime: finishTime,
      duration: finishTime - this.startTimestamp
    };
    return report;
  }
  stopObserver() {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;

      this.reset(false);
    }
  }
  /**
   * 执行上报逻辑
   */
  reportMetrics() {
    const metric = this.getMetrics();
    if (metric && this.report) {
      this.report(metric);
      hbLogger.debug('INP指标结果上报:', metric);
    }
  }
  /**
   * 3. 重置监控状态
   */
  reset(observeReset = true) {
    this.reportMetrics();
    this.interactions = [];
    this.startTimestamp = Date.now();
    if (!this.performanceObserver && observeReset) {
      this.setupPerformanceObserver();
    }
    hbLogger.debug('INP监控已重置');
  }

  /**
   * 4. 销毁监控实例
   */
  destroy() {
    this.isActive = false;

    // 断开 PerformanceObserver
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
    }

    hbLogger.debug('INP监控实例已销毁，资源已释放');
  }

  /**
   * 工具方法
   */
  isValidInteraction(entry) {
    if (!this.isActive || !this.shouldSample()) return false;

    const validEvents = new Set([
      'click', 'keydown', 'keyup', 'keypress',
      'pointerdown', 'pointerup', 'mousedown', 'mouseup',
      'touchstart', 'touchend', 'input', 'change'
    ]);

    return validEvents.has(entry.name) &&
      entry.duration >= this.minDuration;
  }

  shouldSample() {
    return Math.random() <= this.samplingRate;
  }
}

export const inpMonitor = new INPMonitor();
